/**
 * useNotifications.js — React hook for Web Push notification management
 *
 * Wraps the browser Push API and Directus persistence into a single,
 * easy-to-consume hook. Components call enable() / disable() and read
 * isSubscribed to render the notification toggle.
 *
 * Usage:
 *   const { isSupported, permission, isSubscribed, isLoading, enable, disable } = useNotifications();
 *
 * State is derived from the live browser subscription on mount, so a page
 * reload reflects the real subscription state without a round-trip to Directus.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  isPushSupported,
  checkPermission,
  getServiceWorkerRegistration,
  subscribeToPush,
  unsubscribeFromPush,
  getCurrentSubscription,
} from '../api/notifications';
import { savePushSubscription } from '../api/directus';

export function useNotifications() {
  const [isLoading, setIsLoading]     = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission]   = useState(checkPermission());
  const [error, setError]             = useState(null);

  // On mount, check whether a push subscription exists in the browser AND
  // ensure it is present in Directus. If the Directus record was deleted
  // (e.g. manually wiped) while the browser still holds the subscription,
  // we re-save it so the Worker can deliver again.
  useEffect(() => {
    if (!isPushSupported()) {
      setIsLoading(false);
      return;
    }
    getServiceWorkerRegistration()
      .then(async (reg) => {
        const sub = await getCurrentSubscription(reg);
        if (!sub) {
          setIsSubscribed(false);
          return;
        }
        // savePushSubscription is idempotent — it skips the POST if the
        // endpoint already exists in Directus, so this is safe every load.
        await savePushSubscription(sub);
        setIsSubscribed(true);
      })
      .catch(() => setIsSubscribed(false))
      .finally(() => setIsLoading(false));
  }, []);

  /**
   * Request permission, create a push subscription, and save it to Directus.
   * Safe to call repeatedly — if the browser already has a subscription it is
   * reused and Directus is only written to if no matching endpoint exists yet.
   * Resolves to true on success, false if the user denied permission.
   */
  const enable = useCallback(async () => {
    if (!isPushSupported()) return false;
    setIsLoading(true);
    setError(null);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        setError('Notification permission was denied. Please enable it in your browser settings.');
        return false;
      }

      const reg = await getServiceWorkerRegistration();
      const sub = await subscribeToPush(reg);
      await savePushSubscription(sub);

      setIsSubscribed(true);
      return true;
    } catch (err) {
      // err may be a standard Error, a Directus payload {errors:[{message}]},
      // or something else entirely — extract the best available string.
      const msg = err?.message ?? err?.errors?.[0]?.message ?? JSON.stringify(err);
      console.error('[useNotifications] enable failed:', msg);
      setError(msg);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isSupported: isPushSupported(),
    permission,
    isSubscribed,
    isLoading,
    error,
    enable,
  };
}
